import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import PageTemplate from "../../../templates/PageTemplate";
import PlanUsageChartContainer from "./PlanUsageChartContainer";
import UsageCard from "./UsageCard";
import Card from "../../../theme/untitled/Card/Card";
import Button from "../../../theme/untitled/Button/Button";
import { QUOTAS, UsageFieldsMapping } from "../../../../../consts";
import { useCurrentOrganizationStore } from "../../../../../hooks/redux/currentOrganization";
import { hasSubscription } from "../../../helpers/organization";
import appConfig from "../../../../../appConfig";
import styles from "./PlanUsage.module.scss";

const numberFormatter = Intl.NumberFormat(window.navigator.locale || "en-US");
const PlanUsagePage = () => {
  const { currentOrganization } = useCurrentOrganizationStore();
  const history = useHistory();

  const subscription = hasSubscription(currentOrganization);

  const quotas = subscription.quotas;

  const goToBilling = useCallback(() => history.push("/billing"), [history]);

  return (
    <PageTemplate
      sidebarId="planUsage"
      title={"Reports: Plan Usage"}
      icon={"query_stats"}
      subtitle={
        "Your current usage against plan limits. Resets vary by resource — see each card for details."
      }
      headerControls={""}
    >
      <div className={styles.page}>
        <PlanUsageChartContainer />

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Account resources</h3>
          <div className={styles.cardsGrid}>
            <UsageCard
              title={"Published widgets"}
              description={
                "Active widgets currently embedded or shared via link. Unpublished drafts don't count toward this limit."
              }
              usage={Number(
                currentOrganization.model.usage[
                  UsageFieldsMapping[QUOTAS.widgets]
                ]
              )}
              limit={Number(quotas.widgets_limit)}
            />
            <UsageCard
              title={"Website domains"}
              description={
                "The number of websites you can embed widgets on. Each domain is associated with your account."
              }
              usage={Number(
                currentOrganization.model.usage[
                  UsageFieldsMapping[QUOTAS.domains]
                ]
              )}
              limit={Number(quotas.domains_limit)}
            />
            <UsageCard
              title={"Team members"}
              description={
                "Colleagues who have access to your Revisual account. Each member can manage events and widgets."
              }
              usage={Number(
                currentOrganization.model.usage[UsageFieldsMapping[QUOTAS.team]]
              )}
              limit={Number(quotas.team_limit)}
            />
            <UsageCard
              title={"Integrations"}
              description={
                "Connected data sources (e.g. Google Calendar, external feeds) that automatically sync data to your widgets."
              }
              usage={Number(
                currentOrganization.model.usage[
                  UsageFieldsMapping[QUOTAS.integrations]
                ]
              )}
              limit={Number(quotas.integrations_limit)}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Fixed limits</h3>
          <div className={styles.cardsGrid}>
            <Card>
              <Card.Header title={"Synced events / month"} />
              <Card.Body>
                <p className={styles.fixedLimitValue}>
                  {numberFormatter.format(Number(quotas.events_limit))} events
                </p>
                <p className={styles.fixedLimitDescription}>
                  Number of individual event records synced from all your
                  connected integrations each month. Not currently tracked —
                  unlimited for now.
                </p>
              </Card.Body>
            </Card>

            <Card>
              <Card.Header title={"Future events pulled"} />
              <Card.Body>
                <p className={styles.fixedLimitValue}>12 months ahead</p>
                <p className={styles.fixedLimitDescription}>
                  How far into the future {appConfig.displayName} fetches events
                  from your sources. This is a hard limit and cannot currently
                  be customised.
                </p>
              </Card.Body>
            </Card>
          </div>
        </div>

        <Card className={styles.upgradeBanner}>
          <Card.Body className={styles.upgradeBannerBody}>
            <p className={styles.upgradeBannerText}>
              <strong>Need more?</strong> Upgrade your plan to increase limits
              on integrations, published widgets, and widget views.
            </p>
            <Button hierarchy={"secondaryGray"} onClick={goToBilling}>
              Upgrade plan
            </Button>
          </Card.Body>
        </Card>
      </div>
    </PageTemplate>
  );
};

export default PlanUsagePage;
